import AutorenewIcon from '@mui/icons-material/Autorenew';
import HistoryIcon from '@mui/icons-material/History';
import { Alert, Box, Chip, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import BackendService from 'BackendService';
import { useJobs } from 'JobContext';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ArticleCard from '../../components/ArticleCard';
import StyledSection from '../../components/styles/StyledSection';
import { StyledHeading } from '../../components/styles/styles';
import Pagination, { PaginationData } from '../../components/utils/Pagination';
import { Article } from '../../models/Article';

interface ArticlesResponse {
  data: Article[];
  pagination: PaginationData;
}

const AllArticles: React.FC = () => {
  const navigate = useNavigate();
  const { jobs, refreshJobs } = useJobs();
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [regeneratingArticles, setRegeneratingArticles] = useState<Set<string>>(new Set());

  const hasRunningJob = (articleId: string): boolean => {
    if (!jobs?.runningJobs) return false;
    return jobs.runningJobs.some(
      (job) =>
        job.linkedModelType === 'Article' &&
        job.linkedModelId === articleId &&
        (job.status === 'IN_PROGRESS' || job.status === 'PENDING'),
    );
  };

  const loadArticles = async (page: number = 1) => {
    try {
      setLoading(true);
      const response = await new BackendService().getAllArticles(page);
      const articlesData: ArticlesResponse = response;

      setArticles(articlesData.data);
      setPagination(articlesData.pagination);
      setCurrentPage(page);
      setError(null);
    } catch (err) {
      console.error('Error loading articles:', err);
      setError('Failed to load articles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    loadArticles(newPage);
  };

  const handleRegenerate = async (article: Article, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent navigation to article detail

    try {
      setRegeneratingArticles((prev) => new Set(prev).add(article.id));

      await new BackendService().refineArticleContent(
        article.id,
        null, // message
        [], // chatHistory
        true, // isRegeneration
      );

      // Wait for the job to appear in the job list
      let jobFound = false;
      let attempts = 0;
      const maxAttempts = 30; // 30 seconds maximum wait

      while (!jobFound && attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait 1 second

        // Refresh jobs to get the latest data
        refreshJobs();

        // Check if a job for this article has appeared
        if (hasRunningJob(article.id)) {
          jobFound = true;
          break;
        }

        attempts++;
      }

      // Reload the specific article to show updated status
      try {
        const updatedArticle = await new BackendService().getArticle(article.id);
        setArticles((prev) => prev.map((a) => (a.id === article.id ? updatedArticle : a)));
      } catch (reloadError) {
        console.error('Error reloading article:', reloadError);
        // Fallback to reloading all articles if individual reload fails
        await loadArticles(currentPage);
      }
    } catch (err) {
      console.error('Error regenerating article:', err);
      setError('Failed to regenerate article. Please try again.');
    } finally {
      setRegeneratingArticles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(article.id);
        return newSet;
      });
    }
  };

  useEffect(() => {
    loadArticles(1);
  }, []);

  const handleArticleClick = (article: Article) => {
    navigate(`/articles/${article.id}`);
  };

  const handleActivityLog = (article: Article, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the article click
    navigate(`/activityLogs/ARTICLE/${article.id}`);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'published':
        return 'success';
      case 'pending preview':
        return 'warning';
      case 'failed':
        return 'error';
      case 'archived':
        return 'default';
      default:
        return 'info';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const shouldShowRegenerateButton = (status: string) => {
    const statusUpper = status.toUpperCase();
    return statusUpper === 'DRAFT' || statusUpper === 'PENDING_GENAI' || statusUpper === 'FAILED';
  };

  if (loading && articles.length === 0) {
    return (
      <Box sx={{ pt: 5, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 3, pb: 5 }}>
      <StyledHeading>All Articles</StyledHeading>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {articles.length === 0 && !loading ? (
        <StyledSection tone="tone1">
          <Typography variant="body1" sx={{ textAlign: 'center', py: 4 }}>
            No articles found.
          </Typography>
        </StyledSection>
      ) : (
        <>
          <StyledSection tone="tone1" mb="4">
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(auto-fit, minmax(380px, 1fr))',
                },
                gap: 3,
                width: '100%',
                overflow: 'hidden',
              }}
            >
              {articles.map((article) => (
                <Box key={article.id} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        transition: 'transform 0.2s ease-in-out',
                      },
                    }}
                    onClick={() => handleArticleClick(article)}
                  >
                    <ArticleCard
                      tone="tone1"
                      content={{
                        imageCarousel: {
                          type: 'ImageCarousel',
                          id: 'default',
                          properties: {
                            id: 'default',
                            heading: { id: 'default', content: '' },
                            items: [
                              {
                                heading: {
                                  id: 'default',
                                  content: article.title || article.id || 'Untitled Article',
                                },
                                text: { id: 'default', content: '' },
                                imageUrl: {
                                  id: 'default',
                                  url: '/assets/coming_soon.jpg',
                                },
                                altText: { id: 'default', content: '' },
                                summaryImage: true,
                              },
                            ],
                            footnotes: [],
                          },
                        },
                        headline: {
                          type: 'StandardHeaderText',
                          id: 'default',
                          properties: {
                            id: 'default',
                            heading: {
                              id: 'default',
                              content: article.title || article.id || 'Untitled Article',
                            },
                            content: { id: 'default', content: '' },
                          },
                        },
                        extendedSummary: [],
                      }}
                      linkTo={`/articles/${article.id}`}
                      linkText="View"
                    />
                    <Box
                      sx={{
                        mt: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        maxWidth: { xs: '100%', lg: '360px' },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={article.status}
                          color={getStatusColor(article.status) as any}
                          size="small"
                          variant="outlined"
                        />
                        {shouldShowRegenerateButton(article.status) && (
                          <Tooltip
                            title={
                              hasRunningJob(article.id)
                                ? 'Article is being processed'
                                : 'Regenerate Article'
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => handleRegenerate(article, e)}
                              disabled={
                                regeneratingArticles.has(article.id) || hasRunningJob(article.id)
                              }
                              sx={{
                                color: 'primary.main',
                                backgroundColor: 'background.paper',
                                border: '1px solid',
                                borderColor: 'primary.main',
                                '&:hover': {
                                  backgroundColor: 'primary.main',
                                  color: 'primary.contrastText',
                                },
                                '&:disabled': {
                                  color: 'text.disabled',
                                  borderColor: 'text.disabled',
                                },
                              }}
                            >
                              {regeneratingArticles.has(article.id) || hasRunningJob(article.id) ? (
                                <CircularProgress size={16} />
                              ) : (
                                <AutorenewIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Activity Log">
                          <IconButton
                            size="small"
                            onClick={(e) => handleActivityLog(article, e)}
                            sx={{
                              color: 'text.secondary',
                              backgroundColor: 'background.paper',
                              border: '1px solid',
                              borderColor: 'divider',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                                color: 'primary.main',
                              },
                            }}
                          >
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(article.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </StyledSection>

          {/* Pagination */}
          {pagination && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
                loading={loading}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default AllArticles;
